import React, { useState } from "react";
import { Box, Typography, CircularProgress, Fade, Chip } from "@mui/material";
import dayjs from "dayjs";
import CalendarHeader from "./CalendarHeader";
import CalendarWeekdayLabels from "./CalendarWeekdayLabels";
import CalendarLegend from "./CalendarLegend";
import CalendarGridRows from "./CalendarGridRows";
import { useAppConfig } from "../../../context/AppConfigContext";
import { CUTOFF_HOUR, MAX_MONTHS_AHEAD } from "./constants";

const DateSelector = ({
  selectedDate,
  onDateChange,
  calendarMonth,
  setCalendarMonth,
  availableSlotsMap = {},
  isDateDisabled = () => false,
  loadingMonth = false,
}) => {
  const { config } = useAppConfig();
  const colorRules = config?.availability?.colorRules || [];
  const now = dayjs();
  const today = dayjs().startOf("day");
  const cutoff = today.hour(CUTOFF_HOUR);
  const minDate = now.isAfter(cutoff) ? today.add(1, "day") : today;
  const maxDate = today.add(MAX_MONTHS_AHEAD, "month");

  const [monthKey, setMonthKey] = useState(0);

  const handleDateSelect = (date) => {
    onDateChange(date);
  };

  const handleMonthChange = (delta) => {
    const newMonth = calendarMonth.add(delta, "month");
    setCalendarMonth(newMonth);
    setMonthKey((k) => k + 1);
  };

  const extendedIsDateDisabled = (date) => {
    // User-defined disabled logic
    if (isDateDisabled(date)) return true;

    // Disable today always
    if (date.isSame(today, "day")) return true;

    // Business logic: block next-day if after 2pm
    const isTomorrow = date.isSame(today.add(1, "day"), "day");
    if (isTomorrow && now.isAfter(cutoff)) return true;

    return false;
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Select Date
      </Typography>

      <Box
        sx={{
          mt: 0.5,
          mb: 2,
          p: 1,
          bgcolor: "#fff8e1",
          color: "#8a6d3b",
          fontSize: "13px",
          fontWeight: 500,
          borderRadius: "12px",
        }}>
        <Typography variant="caption">
          Next-day pickup is only available if you order before 2:00 PM today
        </Typography>
      </Box>

      <CalendarHeader
        calendarMonth={calendarMonth}
        minDate={minDate}
        maxDate={maxDate}
        onMonthChange={handleMonthChange}
      />

      <CalendarWeekdayLabels />
      <CalendarLegend colorRules={colorRules} />

      <Box mt={1}>
        {loadingMonth ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={6}>
            <CircularProgress size={28} />
            <Typography variant="body2" mt={2}>
              Loading calendar...
            </Typography>
          </Box>
        ) : (
          <Fade in key={monthKey} timeout={300}>
            <Box>
              <CalendarGridRows
                calendarMonth={calendarMonth}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                availableSlotsMap={availableSlotsMap}
                isDateDisabled={extendedIsDateDisabled}
                minDate={minDate}
                maxDate={maxDate}
                today={today}
                colorRules={colorRules}
              />
            </Box>
          </Fade>
        )}
      </Box>
    </Box>
  );
};

export default DateSelector;
